
import { supabase } from '@/integrations/supabase/client';
import { FeaturedTrack } from '@/types/featuredTracks';

export async function fetchAllFeaturedTracks() {
  console.log("featuredTracksService: Fetching all featured tracks");
  const { data, error: functionError } = await supabase.functions.invoke('featured-tracks', {
    body: { action: 'fetch' }
  });
  
  if (functionError) {
    console.error('Error invoking featured-tracks function:', functionError);
    throw new Error(functionError.message || 'Failed to fetch featured tracks');
  }
  
  if (!data || !data.success) {
    console.error('Error fetching featured tracks:', data?.error);
    throw new Error(data?.error || 'Failed to fetch featured tracks');
  }
  
  console.log("featuredTracksService: Fetched tracks successfully:", data.tracks);
  return data.tracks as FeaturedTrack[];
}

export async function fetchFeaturedTracksByUserId(userId: string) {
  console.log("featuredTracksService: Fetching tracks for user:", userId);
  const { data, error: functionError } = await supabase.functions.invoke('featured-tracks', {
    body: { 
      action: 'fetch-by-user',
      userId
    }
  });
  
  if (functionError) {
    console.error('Error invoking featured-tracks function for user tracks:', functionError);
    throw new Error(functionError.message || 'Failed to fetch featured tracks');
  }
  
  console.log("featuredTracksService: Response from fetch-by-user:", data);
  
  if (!data || !data.success) {
    console.error('Error fetching user tracks:', data?.error);
    throw new Error(data?.error || 'Failed to fetch featured tracks');
  }
  
  if (data.tracks && data.tracks.length > 0) {
    console.log("featuredTracksService: First track:", data.tracks[0]);
  } else {
    console.log("featuredTracksService: No tracks returned for user");
  }
  
  return data.tracks as FeaturedTrack[] || [];
}

export async function addFeaturedTrack(trackUrl: string, session: any) {
  if (!session) {
    throw new Error('Authentication required');
  }

  const { data, error: functionError } = await supabase.functions.invoke('featured-tracks', {
    body: {
      action: 'add',
      trackUrl
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`
    }
  });
  
  if (functionError) {
    console.error('Error invoking featured-tracks function:', functionError);
    throw new Error(functionError.message || 'Failed to add track');
  }
  
  if (!data || !data.success) {
    console.error('Add track response:', data);
    throw new Error(data?.error || 'Failed to add track');
  }
  
  return data;
}

export async function deleteFeaturedTrack(trackId: string, session: any) {
  if (!session) {
    throw new Error('Authentication required');
  }
  
  console.log('Sending delete request to edge function for track:', trackId);
  
  const { data, error: functionError } = await supabase.functions.invoke('featured-tracks', {
    body: {
      action: 'delete',
      trackId
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`
    }
  });
  
  if (functionError) {
    console.error('Error invoking featured-tracks function:', functionError);
    throw new Error(functionError.message || 'Failed to remove track');
  }
  
  if (!data || !data.success) {
    throw new Error(data?.error || 'Failed to remove track');
  }
  
  return data;
}

export async function reorderFeaturedTracks(updatedTracks: FeaturedTrack[], session: any) {
  if (!session) {
    throw new Error('Authentication required');
  }
  
  const { data, error: functionError } = await supabase.functions.invoke('featured-tracks', {
    body: {
      action: 'reorder',
      tracks: updatedTracks.map(track => ({ 
        id: track.id, 
        position: track.position 
      }))
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`
    }
  });
  
  if (functionError) {
    console.error('Error invoking featured-tracks function:', functionError);
    throw new Error(functionError.message || 'Failed to update track order');
  }
  
  if (!data || !data.success) {
    throw new Error(data?.error || 'Failed to update track order');
  }
  
  return data;
}
